import {
  Box,
  Flex,
  IconButton,
  InputDescription,
  InputGroup,
  InputGroupLabel,
} from '@fullstory/components';
import { intl } from '@fullstory/intl';
import React, { useCallback } from 'react';
import { URLRuleAutoComplete } from '@fullstory/pages-shared/components/URLRuleAutoComplete';

const QUERY_PARAM_SIGNIFIER = '?';
const QUERY_PARAM_EQUAL = '=';

interface URLRuleQueryParamProps {
  value: { key: string; value: string };
  error?: { message: string; value: boolean };
  onChange: (value: { key: string; value: string }) => void;
  onClear: () => void;
  onAnyFocus?: () => void;
  getKeySuggestions: (value: string) => Promise<string[]>;
  getValueSuggestions: (key: string, value: string) => Promise<string[]>;
}

export const URLRuleQueryParam: React.FC<URLRuleQueryParamProps> = ({
  value,
  error: _error,
  onChange,
  onClear,
  onAnyFocus,
  getKeySuggestions,
  getValueSuggestions: _getValueSuggestions,
}) => {
  const handleOnChangeKey = useCallback(
    (key: string) => {
      onChange({ ...value, key });
    },
    [value, onChange],
  );
  const handleOnChangeValue = useCallback(
    (val: string) => {
      onChange({ ...value, value: val });
    },
    [value, onChange],
  );

  const getValueSuggestions = useCallback(
    (inputValue: string): Promise<string[]> => _getValueSuggestions(value.key, inputValue),
    [value.key, _getValueSuggestions],
  );

  const [canShowErrors, setCanShowErrors] = React.useState(!!value.key || !!value.value);
  const onAnyBlur = useCallback(() => {
    setCanShowErrors(true);
  }, []);

  const errorMessage = canShowErrors ? _error?.message : undefined;
  const invalidKey = canShowErrors ? !!_error && !_error.value : false;
  const invalidValue = canShowErrors ? !!_error && _error.value : false;

  return (
    <Box>
      <Flex justifyContent="stretch" alignItems="center" gap={4}>
        <InputGroup style={{ flexGrow: 1 }}>
          <InputGroupLabel>{QUERY_PARAM_SIGNIFIER}</InputGroupLabel>
          <URLRuleAutoComplete
            value={value.key}
            onChange={handleOnChangeKey}
            onBlur={onAnyBlur}
            onFocus={onAnyFocus}
            placeholder={intl.formatMessage({
              defaultMessage: 'Enter key, e.g. "tab"',
              id: 'bh9mv7',
            })}
            invalid={invalidKey}
            autoFocusInput={!value.key}
            getSuggestions={getKeySuggestions}
            testId="auto-complete-query-key"
          />
          <InputGroupLabel>{QUERY_PARAM_EQUAL}</InputGroupLabel>
          <URLRuleAutoComplete
            value={value.value}
            onChange={handleOnChangeValue}
            onBlur={onAnyBlur}
            onFocus={onAnyFocus}
            placeholder={intl.formatMessage({
              defaultMessage: 'Enter value, e.g. "home"',
              id: 'ncPRNg',
            })}
            invalid={invalidValue}
            getSuggestions={getValueSuggestions}
            testId="auto-complete-query-value"
          />
        </InputGroup>
        <IconButton
          iconName="clear"
          title={intl.formatMessage({ defaultMessage: 'Remove query parameter', id: 'SF5adM' })}
          onClick={onClear}
        />
      </Flex>
      {errorMessage && <InputDescription invalid description={errorMessage} />}
    </Box>
  );
};
